/**
 * Allocation Ledger — dispensed volume attributed back to dairies and pathways.
 *
 * The screen the quarterly close runs on, and the one a verifier is walked
 * through. Stations appear in dispensing-feed order because that is the order
 * the allocation consumes them, so the table reads as the algorithm ran: each
 * station is filled from the lot queue until full, and a dairy whose lot spans
 * a station boundary appears at the bottom of one group and the top of the next.
 * Sorting the stations any other way would make the arithmetic look arbitrary.
 *
 * Every figure here is computed by `lib/allocationEngine.ts` from production,
 * capacity and roster order — nothing on this screen is a stored allocation.
 *
 * Backend: there is no `/lcfs/allocation` endpoint. The four reference lists it
 * merges — `/lcfs/dairies`, `/lcfs/fse`, `/lcfs/production`, `/lcfs/capacities`
 * — are real (BIO-380); the merge itself runs here, and porting it to Python is
 * the remaining half of the move (locked decision #6). `VITE_USE_LCFS_MSW`
 * serves those four from `mocks/handlers.ts` for offline mode and tests.
 */

import {
  Alert,
  Badge,
  Divider,
  Group,
  Paper,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useSearchParams } from "react-router-dom";
import { useAllocationPeriod } from "../api/hooks";
import type { AllocationStationGroup } from "../api/types";
import { ClosureBanner } from "../components/ClosureBanner";
import { ConfirmationCard } from "../components/lcfs/ConfirmationCard";
import { DairyHistoryDrawer } from "../components/lcfs/DairyHistoryDrawer";
import { PeriodLoadCard } from "../components/lcfs/PeriodLoadCard";
import { PeriodSupportBadge, PeriodSupportCard } from "../components/lcfs/PeriodSupportCard";
import { StationHistoryDrawer } from "../components/lcfs/StationHistoryDrawer";
import { useLcfsDrilldown } from "../components/lcfs/useLcfsDrilldown";
import { periodLabelFrom } from "../lib/datetime";
import { formatApiError } from "../lib/errors";
import { fmtMmbtu as fmt, isZeroMmbtu as isZero } from "../lib/mmbtuFormat";

export function AllocationLedgerPage() {
  // Period is state-of-record in the URL so a close can be linked to.
  const [params, setParams] = useSearchParams();
  const period = params.get("period") ?? undefined;

  const ledgerQ = useAllocationPeriod(period);
  const drill = useLcfsDrilldown();

  const setPeriod = (value: string) => {
    const p = new URLSearchParams(params);
    if (value) p.set("period", value);
    else p.delete("period");
    setParams(p, { replace: true });
  };

  const data = ledgerQ.data;

  return (
    <Stack gap="lg">
      {/* No page heading: the tab label is it — see `routes/Lcfs.tsx`. Two
          sections, split because the page read as one form: a partner user
          picked a month in the selector and then looked for the upload it
          belonged to. The uploads here take no month — the workbook names its
          own months and a confirmation restates every month of the term — so
          they sit above, apart from anything the selector controls. */}
      <Stack gap="sm" data-test="lcfs-upload-section">
        <SectionHeading
          title="Upload"
          description="Load new months from the partner's workbook. It lists the months it holds and you tick which to load — nothing below needs choosing first."
        />
        <PeriodLoadCard />
        {/* Outside the period selector on purpose: a confirmation states every
            month of the term, so filing the July document can turn June red. */}
        <ConfirmationCard />
      </Stack>

      <Divider />

      {/* Everything from here down is one loaded month: its statements, its
          closure and its figures all follow the selector. */}
      <Stack gap="lg" data-test="lcfs-loaded-month-section">
        <Group justify="space-between" wrap="wrap" gap="md" align="flex-end">
          <SectionHeading
            title="Loaded months"
            description="Review a month that is already in the ledger, and file its utility statements. Choosing a month here only changes what you are viewing — it uploads nothing."
          />
          {data ? (
            <Group gap="sm" wrap="nowrap" align="flex-end">
              <Select
                label="Viewing month"
                aria-label="Viewing month"
                data={data.available_periods.map((p) => ({
                  value: p,
                  label: periodLabelFrom(p),
                }))}
                value={data.period}
                onChange={(v) => setPeriod(v ?? "")}
                w={180}
                allowDeselect={false}
                comboboxProps={{ withinPortal: true }}
              />
              {/* Green here means the month's utility statements are all in, not
                  that the allocation closes — that is the ClosureBanner below. */}
              <PeriodSupportBadge period={data.period} />
            </Group>
          ) : null}
        </Group>

        {/* Above the ledger: a month is loaded, then its support is filed, then
            the figures are read. */}
        {data ? <PeriodSupportCard period={data.period} /> : null}

        {ledgerQ.isPending ? (
          <Skeleton height={420} radius="md" />
        ) : ledgerQ.isError ? (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm">{formatApiError(ledgerQ.error)}</Text>
          </Alert>
        ) : !data ? null : (
          <>
            <Text c="dimmed" size="sm" maw={800}>
              Dispensed volume attributed back to individual dairies, and thus to pathway codes.
              Stations appear in dispensing-feed order — the order the allocation consumes them — so
              a dairy whose volume spans two stations appears at the end of one and the start of the
              next.
            </Text>

            <ClosureBanner closure={data.closure} banked={data.banked.value} />

            <Paper p="md" radius="md" withBorder>
              <Group justify="space-between" wrap="wrap" gap="lg">
                <Stat
                  label="Allocated"
                  value={`${fmt(data.allocated.value)} MMBtu`}
                  sub="dispensed at the stations"
                />
                <Stat
                  label="Claimed capacity"
                  value={`${fmt(data.capacity.value)} MMBtu`}
                  sub={`${data.stations.length} stations`}
                />
                <Stat
                  label="Carried forward"
                  value={`${fmt(data.banked.value)} MMBtu`}
                  sub="opens the next period"
                />
                <Stat
                  label="Rows emitted"
                  value={String(data.row_count)}
                  sub={`${data.lots_touched} lots touched`}
                />
              </Group>
            </Paper>

            <Paper radius="md" withBorder>
              <ScrollArea>
                <Table striped={false} highlightOnHover verticalSpacing="xs" miw={880}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Dairy</Table.Th>
                      <Table.Th>Vintage</Table.Th>
                      <Table.Th>Pathway code</Table.Th>
                      <Table.Th ta="right">MMBtu</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data.stations.map((group) => (
                      <StationGroup
                        key={group.aemetis_fse_id}
                        group={group}
                        period={data.period}
                        onOpenStation={drill.openStation}
                        onOpenDairy={drill.openDairy}
                      />
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Paper>

            <Text size="xs" c="dimmed">
              Computed {data.computed_at.slice(0, 10)} from production, station capacity and dairy
              roster order. Volumes are never recomputed in the browser.
            </Text>
          </>
        )}
      </Stack>

      <StationHistoryDrawer
        fseId={drill.stationId}
        onClose={drill.closeStation}
        onOpenDairy={drill.openDairy}
      />
      <DairyHistoryDrawer
        dairyId={drill.dairyId}
        onClose={drill.closeDairy}
        onOpenStation={drill.openStation}
      />
    </Stack>
  );
}

function SectionHeading({ title, description }: { title: string; description: string }) {
  return (
    <Stack gap={2} maw={720}>
      <Text fw={700} size="lg">
        {title}
      </Text>
      <Text size="sm" c="dimmed">
        {description}
      </Text>
    </Stack>
  );
}

function StationGroup({
  group,
  period,
  onOpenStation,
  onOpenDairy,
}: {
  group: AllocationStationGroup;
  period: string;
  onOpenStation: (fseId: string) => void;
  onOpenDairy: (dairyId: string) => void;
}) {
  const clean = isZero(group.residual.value);

  return (
    <>
      <Table.Tr
        style={{
          backgroundColor: "var(--mantine-color-default-hover)",
          borderLeft: `3px solid var(--mantine-color-${clean ? "teal" : "orange"}-6)`,
        }}
      >
        <Table.Td colSpan={3}>
          <Group gap="xs" wrap="wrap">
            <Text
              fw={600}
              size="sm"
              c="blue"
              style={{ cursor: "pointer" }}
              onClick={() => onOpenStation(group.aemetis_fse_id)}
            >
              {group.station}
            </Text>
            <Text ff="monospace" size="xs" c="dimmed">
              {group.aemetis_fse_id}
            </Text>
            {!clean ? (
              <Tooltip
                label="Unallocated remainder at this station. If volume was still banked this is an allocation fault; if the lot queue ran dry, production was short of the claim."
                multiline
                w={300}
                withArrow
              >
                <Badge color="orange" variant="light" size="sm">
                  residual {fmt(group.residual.value)}
                </Badge>
              </Tooltip>
            ) : null}
          </Group>
        </Table.Td>
        <Table.Td ta="right">
          <Text size="xs" c="dimmed" ff="monospace">
            cap {fmt(group.capacity.value)}
          </Text>
        </Table.Td>
        <Table.Td />
      </Table.Tr>

      {group.rows.map((row, idx) => (
        <Table.Tr key={`${group.aemetis_fse_id}:${row.dairy_id}:${row.vintage_month}:${idx}`}>
          <Table.Td>
            <Text
              size="sm"
              pl="md"
              c="blue"
              style={{ cursor: "pointer" }}
              onClick={() => onOpenDairy(row.dairy_id)}
            >
              {row.dairy_name}
            </Text>
          </Table.Td>
          <Table.Td>
            <Group gap={6} wrap="nowrap">
              <Text size="sm" ff="monospace">
                {row.vintage_month}
              </Text>
              {row.vintage_month !== period ? (
                <Tooltip
                  label={`Banked ${row.age_periods} period${row.age_periods === 1 ? "" : "s"} before being dispensed`}
                  withArrow
                >
                  <Badge color="grape" variant="light" size="xs">
                    +{row.age_periods}
                  </Badge>
                </Tooltip>
              ) : null}
            </Group>
          </Table.Td>
          <Table.Td>
            <Text size="xs" ff="monospace" c="dimmed">
              {row.fpc_code}
            </Text>
          </Table.Td>
          <Table.Td ta="right">
            <Text size="sm" ff="monospace" style={{ fontVariantNumeric: "tabular-nums" }}>
              {fmt(row.mmbtu.value)}
            </Text>
          </Table.Td>
        </Table.Tr>
      ))}
    </>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
        {label}
      </Text>
      <Text size="lg" fw={700} style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Text>
      <Text size="xs" c="dimmed">
        {sub}
      </Text>
    </Stack>
  );
}
